import { type ReactNode, useState } from 'react';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  Bank,
  ChartLineUp,
  ListChecks,
  Settings,
  Moon,
  Sun,
  ArrowLeft,
} from '~/components/icons';
import { useTranslation } from 'react-i18next';
import { signOut, triggerHaptic, track, reset } from '@repo/shared/client';
import { APP_NAME, canEditData, errorTracker, type OrganizationRole } from '@repo/shared/common';
import { sessionQueryOptions } from '~/lib/queries/session';
import {
  currentOrganizationQueryOptions,
  ipAccessStatusQueryOptions,
} from '~/lib/queries/organization';
import { OrgCardMenu } from '~/components/shell/OrgCardMenu';
import { UserCardMenu } from '~/components/shell/UserCardMenu';
import { OnboardingSuccessModal } from '~/components/onboarding/OnboardingSuccessModal';
import { PendingInvitationsPrompt } from '~/components/organizations/PendingInvitationsPrompt';
import { usePageChromeStore } from '~/lib/stores/page-chrome';
import { useSidebarModeState } from '~/lib/hooks/use-sidebar-mode';
import {
  AppShell,
  Sidebar,
  TopBar,
  Drawer,
  Avatar,
  ActionIcon,
  Burger,
  Group,
  useComputedColorScheme,
  useMantineColorScheme,
} from '~/components/ui';

import styles from './_protected.module.css';

export const Route = createFileRoute('/_protected')({
  beforeLoad: async ({ context }) => {
    // Server-resolved auth gate. `ensureQueryData` runs the isomorphic session
    // fetcher (in-process on the server, cached on the client) against the shared
    // ['auth','session'] cache, so an unauthenticated request is redirected
    // before any protected HTML renders — no render-time <Navigate>. The
    // resolved session is returned into the router context for descendants.
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
    if (!session) {
      throw redirect({ to: '/sign-in' });
    }

    // Onboarding gate. A signed-in user only reaches the app proper once their
    // email is verified AND they belong to an organization — otherwise bounce
    // them into the matching onboarding step. The org resolve (`ensureQueryData`)
    // runs `resolveActiveOrganization` (membership-verified, self-healing) and
    // seeds the cache, so descendants read org/role warm.
    if (!session.user.emailVerified) {
      throw redirect({ to: '/onboarding/verify-email' });
    }
    const { organization, role } = await context.queryClient.ensureQueryData(
      currentOrganizationQueryOptions(),
    );
    if (!organization) {
      throw redirect({ to: '/onboarding/create-organization' });
    }

    // IP-allowlist gate. If the active org restricts authenticated traffic to an
    // allowlist and this client's IP isn't on it, redirect to the (session-only,
    // NOT IP-gated) blocked page. Resolved through the isomorphic ip-access-status
    // read so it holds on both SSR (real edge IP resolved in-process) and client
    // navigation (a real same-origin request the Worker sees with the true
    // CF-Connecting-IP — the browser can't read its own edge IP). Org-scoped APIs
    // are independently blocked at `requireActiveOrg`, so this is the UX half of a
    // boundary enforced on both pages and API.
    const ipAccess = await context.queryClient.ensureQueryData(ipAccessStatusQueryOptions());
    if (ipAccess.hasAllowlist && !ipAccess.allowed) {
      throw redirect({ to: '/blocked' });
    }

    // Extend the context with the resolved org + role so descendants can gate on
    // role without a second fetch. The original `{ session }` shape is preserved.
    return { session, organization, role };
  },
  component: ProtectedLayout,
});

type ShellVariant = 'desktop' | 'mobile';

// The app's information architecture. `to` values are string literals so
// TanStack's `navigate` accepts them as known routes; each has a matching route
// under `_protected/`. Add your own entries here as you build features.
//
// `requiresEdit: true` marks a data-mutation surface (the uploader, fields):
// it's filtered out for viewers — who can read the portfolio but not change it
// (mirrors the same gate the route guards enforce server-side via `canEditData`).
//
// Modeling is the default landing surface. Settings is NOT here — it's pinned to
// the bottom of the rail (above the user card) via `SETTINGS_NAV`.
const PRIMARY_NAV = [
  { to: '/modeling', labelKey: 'nav.modeling', Icon: ChartLineUp },
  { to: '/uploads', labelKey: 'nav.uploads', Icon: Upload, requiresEdit: true },
  { to: '/portfolio', labelKey: 'nav.portfolio', Icon: Bank },
  { to: '/fields', labelKey: 'fields.navLabel', Icon: ListChecks, requiresEdit: true },
] as const;

// Pinned to the bottom of the rail, above the user card + divider. Shown to
// everyone (read-only org/team for viewers).
const SETTINGS_NAV = { to: '/settings', labelKey: 'nav.settings', Icon: Settings } as const;

// Routes whose content needs the FULL card width (data-dense surfaces: the Modeling
// analytics grid, the Portfolio table) rather than the default reading-width cap.
// Matched by prefix so nested routes inherit it. Resolved synchronously from the
// pathname below (no store/effect) so the width is right on the first paint — no
// max-width→full-width jump when navigating onto these screens.
const FULL_WIDTH_ROUTES = ['/modeling', '/portfolio'] as const;

type ShellRoute =
  | (typeof PRIMARY_NAV)[number]['to']
  | (typeof SETTINGS_NAV)['to'];

function ProtectedLayout(): ReactNode {
  // Auth is gated in `beforeLoad` (above), which also returns the resolved
  // session onto the router context — so reading it here is synchronous and
  // always present. Add a second gate in `beforeLoad` (e.g. require an
  // organization or active subscription) to make `_protected` distinct from the
  // auth-only `_authed`.
  // `organization` is guaranteed non-null by the `_protected` beforeLoad gate;
  // `role` is the caller's membership role (used to gate the nav).
  const { session, organization: gateOrganization, role: gateRole } = Route.useRouteContext();
  // ALSO subscribe to the same current-org cache entry the gate warmed. The
  // router context is a snapshot from `beforeLoad` — after a tenant change
  // (org switch/create clears the query cache) this live observer refetches
  // and re-renders the shell (org name, role-gated nav) with the new org,
  // instead of trusting a stale snapshot until the next full route resolve.
  // Falls back to the gate snapshot while (re)fetching.
  const { data: liveOrg } = useQuery(currentOrganizationQueryOptions());
  const organization = liveOrg?.organization ?? gateOrganization;
  const role = liveOrg?.role ?? gateRole;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const sidebar = useSidebarModeState();
  const isFullWidth = FULL_WIDTH_ROUTES.some((route) => isActivePath(pathname, route));

  const email = session.user.email;
  const displayName = session.user.name?.trim() ? session.user.name : email;
  const organizationName = organization?.name ?? APP_NAME;
  const closeMobileNav = (): void => setMobileNavOpen(false);
  const goToProfile = (): void => {
    triggerHaptic();
    void navigate({ to: '/settings/profile' });
  };

  return (
    <>
      <AppShell
        railCollapsed={!sidebar.baseExpanded}
        railOverlay={sidebar.isOverlay}
        onRailMouseEnter={sidebar.handleMouseEnter}
        onRailMouseLeave={sidebar.handleMouseLeave}
        contentWidth={isFullWidth ? 'full' : 'contained'}
        sidebar={
          <AppSidebar
            variant="desktop"
            email={email}
            displayName={displayName}
            organizationName={organizationName}
            role={role}
            pathname={pathname}
            collapsed={!sidebar.effectiveExpanded}
            onProfile={goToProfile}
          />
        }
        topBar={
          <AppTopBar
            onOpenNav={() => {
              triggerHaptic();
              setMobileNavOpen(true);
            }}
          />
        }
      >
        {/* Entrances are owned by each page's <Reveal> cascade, which fires on
            mount — consistently for SSR first paint, client navigation, and settings
            sub-page switches. No route-level fade wraps the Outlet: a second
            opacity animation here raced the cascade and interrupted it mid-flight. */}
        <Outlet />
      </AppShell>

      {/* Mobile nav: the AppShell rail is hidden below 1280px, so the same
          sidebar is mounted in a left drawer the TopBar burger opens. */}
      <Drawer
        opened={mobileNavOpen}
        onClose={closeMobileNav}
        position="left"
        size="17rem"
        padding={0}
        withCloseButton={false}
        transitionProps={{ duration: 300, transition: 'slide-right' }}
        aria-label={t('shell.mobileNavLabel')}
      >
        <AppSidebar
          variant="mobile"
          email={email}
          displayName={displayName}
          organizationName={organizationName}
          role={role}
          pathname={pathname}
          onNavigate={closeMobileNav}
          onProfile={goToProfile}
        />
      </Drawer>

      {/* One-shot "You're all set" moment, shown over whichever surface the user
          lands on after completing onboarding. */}
      <OnboardingSuccessModal />

      {/* Dismissible prompt for invitations addressed to this (already-onboarded)
          account — existing users never pass the onboarding auto-accept, so this
          is their surface to explicitly accept or decline. */}
      <PendingInvitationsPrompt />
    </>
  );
}

function AppTopBar({ onOpenNav }: { readonly onOpenNav: () => void }): ReactNode {
  const { t } = useTranslation();
  // A page can publish a title + Back action into this docked header (see
  // `usePageChrome`); when set, it replaces the mobile brand and shows on all sizes.
  const pageTitle = usePageChromeStore((state) => state.title);
  const onBack = usePageChromeStore((state) => state.onBack);
  const hasChrome = pageTitle !== null || onBack !== null;

  return (
    <TopBar
      left={
        <Group gap="sm" wrap="nowrap">
          <Burger
            opened={false}
            onClick={onOpenNav}
            size="sm"
            hiddenFrom="lg"
            aria-label={t('nav.toggleMenu')}
            data-testid="mobile-menu-toggle"
          />
          {hasChrome ? (
            <Group gap="xs" wrap="nowrap" data-testid="page-chrome">
              {onBack !== null ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  aria-label={t('shell.back')}
                  data-testid="page-back"
                  onClick={() => {
                    triggerHaptic();
                    onBack();
                  }}
                >
                  <ArrowLeft size={18} />
                </ActionIcon>
              ) : null}
              {pageTitle !== null ? (
                <span className={styles.pageTitle ?? ''}>{pageTitle}</span>
              ) : null}
            </Group>
          ) : (
            <Group gap="sm" wrap="nowrap" hiddenFrom="lg">
              <span className={styles.brandMobile ?? ''}>{APP_NAME}</span>
            </Group>
          )}
        </Group>
      }
      right={<ColorSchemeToggle testId="color-scheme-toggle" />}
    />
  );
}

function AppSidebar({
  variant,
  email,
  displayName,
  organizationName,
  role,
  pathname,
  collapsed = false,
  onNavigate,
  onProfile,
}: {
  readonly variant: ShellVariant;
  readonly email: string;
  readonly displayName: string;
  readonly organizationName: string;
  // The caller's role gates the data-mutation nav entries. Null only mid-resolve
  // (the gate guarantees a membership) — treated as no edit access, fail-closed.
  readonly role: OrganizationRole | null;
  readonly pathname: string;
  /** Icons-only mode (desktop rail). The mobile drawer is always expanded. */
  readonly collapsed?: boolean;
  readonly onNavigate?: (() => void) | undefined;
  readonly onProfile?: (() => void) | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const handleSignOut = useSignOut();

  // Viewers (no edit access) don't see the data-mutation surfaces. Admins and
  // editors see the full nav unchanged.
  const canEdit = role !== null && canEditData(role);
  const navItems = PRIMARY_NAV.filter(
    (item) => !('requiresEdit' in item && item.requiresEdit) || canEdit,
  );
  const settingsActive = isActivePath(pathname, SETTINGS_NAV.to);

  return (
    <Sidebar collapsed={collapsed}>
      {/* The org switcher replaces the static brand wordmark at the top of the
          rail. Wrapped in a padded slot matching Sidebar.Brand's horizontal
          inset so the card aligns with the nav items below. */}
      <div className={styles.brandSlot ?? ''}>
        <OrgCardMenu
          variant={variant}
          organizationName={organizationName}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      </div>
      {/* No eyebrow label on the primary nav — the rail's single nav group needs
          no "Main" heading (the <nav aria-label="Primary"> already names the region). */}
      <Sidebar.Section>
        {navItems.map(({ to, labelKey, Icon }) => (
          <NavItem
            key={to}
            to={to}
            label={t(labelKey)}
            icon={<Icon size={18} weight={isActivePath(pathname, to) ? 'fill' : 'light'} />}
            active={isActivePath(pathname, to)}
            testId={`nav-${to.slice(1)}-${variant}`}
            onNavigate={onNavigate}
          />
        ))}
      </Sidebar.Section>

      {/* Bottom group — pinned to the foot of the rail: Settings, then the user
          card below a divider. The rail's expand/collapse is automatic (viewport
          width + hover-to-expand), so there's no manual toggle here. */}
      <div className={styles.bottomGroup ?? ''}>
        <Sidebar.Section>
          <NavItem
            to={SETTINGS_NAV.to}
            label={t(SETTINGS_NAV.labelKey)}
            icon={<SETTINGS_NAV.Icon size={18} weight={settingsActive ? 'fill' : 'light'} />}
            active={settingsActive}
            testId={`nav-settings-${variant}`}
            onNavigate={onNavigate}
          />
        </Sidebar.Section>
        <Sidebar.Footer>
          <UserCardMenu
            variant={variant}
            collapsed={collapsed}
            displayName={displayName}
            email={email}
            avatar={
              <Avatar tone="green" size="sm" initials={initialsFor(displayName, email)} />
            }
            onSignOut={handleSignOut}
            onProfile={onProfile}
            onBeforeAction={onNavigate}
          />
        </Sidebar.Footer>
      </div>
    </Sidebar>
  );
}

function NavItem({
  to,
  label,
  icon,
  active,
  testId,
  onNavigate,
}: {
  readonly to: ShellRoute;
  readonly label: string;
  readonly icon: ReactNode;
  readonly active: boolean;
  readonly testId: string;
  readonly onNavigate?: (() => void) | undefined;
}): ReactNode {
  return (
    <Sidebar.Item
      as={Link}
      to={to}
      icon={icon}
      active={active}
      data-testid={testId}
      // <Link> owns the navigation: client-side routing, modifier/middle-click
      // ("open in new tab") handling, and — because the router uses
      // defaultPreload: 'intent' — preloading the destination's loader on hover.
      // This handler only fires the side-effects (haptic + closing the mobile drawer).
      onClick={() => {
        triggerHaptic();
        onNavigate?.();
      }}
    >
      {label}
    </Sidebar.Item>
  );
}

function ColorSchemeToggle({ testId }: { readonly testId: string }): ReactNode {
  const { setColorScheme } = useMantineColorScheme();
  // `useComputedColorScheme` resolves the `auto` default to the OS preference,
  // so the icon + first toggle are correct on first load.
  const computed = useComputedColorScheme('light');
  const { t } = useTranslation();
  const isDark = computed === 'dark';

  return (
    <ActionIcon
      variant="subtle"
      size="lg"
      color="gray"
      aria-label={t('shell.themeToggle')}
      data-testid={testId}
      onClick={() => {
        triggerHaptic();
        setColorScheme(isDark ? 'light' : 'dark');
      }}
    >
      {isDark ? <Sun size={18} /> : <Moon size={18} />}
    </ActionIcon>
  );
}

/**
 * Returns a fire-and-forget sign-out handler for the user-card menu. Preserves
 * the original behaviour exactly: reset analytics identity, clear the session,
 * drop all cached server state, then redirect to sign-in (errors reported, not
 * thrown). Drawer-close is handled by the menu's `onBeforeAction`, so this
 * handler only owns the sign-out itself.
 */
function useSignOut(): () => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  async function handleSignOut(): Promise<void> {
    triggerHaptic();
    try {
      track('user_signed_out', {});
      reset();
      await signOut();
      // Drop all cached server state so the next user can't read stale entries.
      queryClient.clear();
      void navigate({ to: '/sign-in' });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(error, { source: 'signOut' });
    }
  }

  return () => void handleSignOut();
}

function isActivePath(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function initialsFor(name: string, email: string): string {
  const source = (name.trim() || email).trim();
  const words = source.split(/\s+/).filter(Boolean);
  const first = words[0] ?? '';
  const second = words[1] ?? '';
  const initials = second
    ? `${first.charAt(0)}${second.charAt(0)}`
    : first.slice(0, 2);
  return initials.toUpperCase() || '?';
}
